import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const handleStartPlanning = () => {
    if (auth.currentUser) {
      navigate('/plan');
    } else {
      navigate('/login');
    }
  };

  const steps = [
    {
      number: 1,
      title: 'Create Your Account',
      description: 'Sign up in seconds and create your wedding profile with basic details about your big day.',
    },
    {
      number: 2,
      title: 'Set Your Preferences',
      description: 'Share your vision using our AI image generation tool to create and visualize your dream wedding style, along with guest count, budget, and timeline.',
    },
    {
      number: 3,
      title: 'Explore Recommendations',
      description: 'Browse personalized vendor and venue recommendations based on your preferences.',
    },
    {
      number: 4,
      title: 'Plan & Organize',
      description: 'Use our tools to manage every aspect of your wedding planning journey with ease.',
    },
  ];

  return (
    <section id="how-it-works" className="py-20 relative overflow-hidden bg-white">
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#FF4E8E]/5 blur-3xl"></div>
      
      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[42px] font-bold font-cormorant-garamond leading-[1.2] text-[#1C1C1E] mb-6">
            How <span className="italic text-[#FF4E8E]">Wedify</span> Works
          </h2>
          <p className="font-cormorant-garamond font-normal text-[16px] text-[#535353]">
            Our streamlined process makes wedding planning intuitive and enjoyable. Get started in minutes.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step) => (
            <div key={step.number} className="bg-gradient-to-br from-[#FF69B4]/15 to-[#FFDAB9]/15 p-6 transition-all duration-300 hover:translate-y-[-3px] group relative w-full rounded-2xl">
              <div className="bg-white p-6 rounded-xl shadow-xl border border-[#FF4E8E]/20">
                <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-[#FF4E8E] flex items-center justify-center font-semibold text-white">
                  {step.number}
                </div>
                <h3 className="text-[18px] font-bold font-cormorant-garamond mb-4 pt-2 text-[#1C1C1E]">{step.title}</h3>
                <p className="text-[15px] font-cormorant-garamond font-normal text-[#535353] mb-6">
                  {step.description.split('. ').map((clause, i, arr) => i === 1 ? <span key={i} className="italic">{clause}</span> : clause + (i < arr.length - 1 ? '. ' : ''))}
                </p>
                <div className="absolute bottom-4 right-4">
                  <CheckCircle className="h-6 w-6 text-[#FF4E8E] opacity-70" />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <button 
            onClick={handleStartPlanning}
            className="bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] hover:opacity-90 text-white px-8 py-4 rounded-lg transition-all duration-300 text-[18px] font-cormorant-garamond font-bold"
          >
            Start Planning Today
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;